import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Analytics } from '@vercel/analytics/react';
import { AssetDirectory } from './components/AssetDirectory.jsx';
import { CatalogTable } from './components/CatalogTable.jsx';
import { DetailDrawer } from './components/DetailDrawer.jsx';
import { FlowFeed } from './components/FlowFeed.jsx';
import { Methodology } from './components/Methodology.jsx';
import { SupplyTable } from './components/SupplyTable.jsx';
import { useMptDiscovery } from './hooks/useMptDiscovery.js';
import { useRwaCatalog } from './hooks/useRwaCatalog.js';
import { useXrplLedger } from './hooks/useXrplLedger.js';
import { publicPrograms, registry, registryAuditDate } from './data/registry.js';
import { buildMatcher } from './lib/xrpl.js';
import { catalogMatcherPrograms, enrichCatalogCoverage } from './lib/catalog.js';

const AssetGlobe = lazy(() => import('./components/AssetGlobe.jsx').then((module) => ({ default: module.AssetGlobe })));
const mobileViews = ['assets', 'map', 'flow'];

function statusCopy(status) {
  return {
    live: 'XRPL mainnet live',
    connecting: 'Connecting to mainnet',
    reconnecting: 'Reconnecting',
    offline: 'Endpoint unavailable',
  }[status] || status;
}

function sourceDate(value) {
  if (!value) return 'date unavailable';
  const normalized = /(?:Z|[+-]\d\d:\d\d)$/.test(value) ? value : `${value}Z`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? value.slice(0, 10) : date.toLocaleDateString();
}

function sourceIsStale(value, maxAgeDays = 7) {
  if (!value) return false;
  const normalized = /(?:Z|[+-]\d\d:\d\d)$/.test(value) ? value : `${value}Z`;
  const timestamp = new Date(normalized).getTime();
  return Number.isFinite(timestamp) && Date.now() - timestamp > maxAgeDays * 86_400_000;
}

export default function App() {
  const initialAssetId = new URLSearchParams(window.location.search).get('asset');
  const discovery = useMptDiscovery();
  const catalog = useRwaCatalog();
  const [scope, setScope] = useState(() => initialAssetId?.startsWith('rwa-xyz-') ? 'catalog' : initialAssetId?.startsWith('metadata-mpt-') ? 'metadata' : 'curated');
  const [selectedId, setSelectedId] = useState(() => initialAssetId || 'rlusd');
  const [drawerOpen, setDrawerOpen] = useState(() => Boolean(initialAssetId));
  const [methodologyOpen, setMethodologyOpen] = useState(false);
  const [mobileView, setMobileView] = useState('assets');
  const [isCompact, setIsCompact] = useState(() => window.matchMedia('(max-width: 760px)').matches);

  const curatedMatcher = useMemo(() => buildMatcher(registry), []);
  const catalogPrograms = useMemo(() => (
    enrichCatalogCoverage(catalog.programs, curatedMatcher, registry)
  ), [catalog.programs, curatedMatcher]);
  const catalogTrackingPrograms = useMemo(() => catalogMatcherPrograms(catalogPrograms), [catalogPrograms]);

  const verifiedMptIds = useMemo(() => new Set(publicPrograms.flatMap((program) =>
    program.instruments.filter((instrument) => instrument.kind === 'mpt').map((instrument) => instrument.issuanceId),
  )), []);
  const metadataPrograms = useMemo(() => discovery.programs.filter((program) =>
    !verifiedMptIds.has(program.instruments[0]?.issuanceId),
  ), [discovery.programs, verifiedMptIds]);
  const metadataSupplies = useMemo(() => discovery.supplies.filter((row) =>
    !verifiedMptIds.has(row.issuanceId),
  ), [discovery.supplies, verifiedMptIds]);
  const allPrograms = useMemo(() => [...registry, ...catalogTrackingPrograms, ...metadataPrograms], [catalogTrackingPrograms, metadataPrograms]);
  const allSelectablePrograms = useMemo(() => [...registry, ...catalogPrograms, ...metadataPrograms], [catalogPrograms, metadataPrograms]);
  const ledger = useXrplLedger(allPrograms, publicPrograms);
  const allSupplies = useMemo(() => [...ledger.supplies, ...metadataSupplies], [ledger.supplies, metadataSupplies]);
  const scopedPrograms = scope === 'metadata' ? metadataPrograms : scope === 'catalog' ? catalogPrograms : registry;
  const scopedSupplies = scope === 'metadata' ? metadataSupplies : ledger.supplies;
  const selectedProgram = allSelectablePrograms.find((program) => program.id === selectedId) || null;

  const trackedWalletCount = useMemo(() => new Set(allPrograms.flatMap((program) =>
    program.instruments.map((instrument) => instrument.issuer),
  ).filter(Boolean)).size, [allPrograms]);
  const activeInstruments = ledger.supplies.filter((row) => row.circulating > 0 && !row.excludeFromActive).length;
  const liveFlowCount = ledger.flows.filter((flow) => flow.source === 'live').length;
  const catalogTrackableCount = catalogPrograms.filter((program) => program.catalog.liveTrackable).length;
  const catalogStale = sourceIsStale(catalog.meta?.sourceUpdatedAt);
  const catalogNotConfigured = catalog.errorCode === 'RWA_CATALOG_NOT_CONFIGURED';

  useEffect(() => {
    const media = window.matchMedia('(max-width: 760px)');
    const onChange = (event) => setIsCompact(event.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target;
      if (target instanceof HTMLElement && ['INPUT', 'SELECT', 'TEXTAREA'].includes(target.tagName)) return;
      const input = document.querySelector('.directory .search-field input');
      if (!input) return;
      event.preventDefault();
      if (window.matchMedia('(max-width: 760px)').matches) setMobileView('assets');
      input.focus();
      input.select();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    const onPopState = () => {
      const asset = new URLSearchParams(window.location.search).get('asset');
      if (asset) {
        setSelectedId(asset);
        if (asset.startsWith('rwa-xyz-')) setScope('catalog');
        else if (asset.startsWith('metadata-mpt-')) setScope('metadata');
        else setScope('curated');
      }
      setDrawerOpen(Boolean(asset));
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const selectProgram = useCallback((id) => {
    setSelectedId(id);
    if (id.startsWith('metadata-mpt-')) setScope('metadata');
    if (id.startsWith('rwa-xyz-')) setScope('catalog');
    setDrawerOpen(true);
    const url = new URL(window.location.href);
    url.searchParams.set('asset', id);
    window.history.replaceState({}, '', url);
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    const url = new URL(window.location.href);
    url.searchParams.delete('asset');
    window.history.replaceState({}, '', url);
  }, []);
  const closeMethodology = useCallback(() => setMethodologyOpen(false), []);

  const moveMobileTab = (event, currentView) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const currentIndex = mobileViews.indexOf(currentView);
    const nextIndex = event.key === 'Home'
      ? 0
      : event.key === 'End'
        ? mobileViews.length - 1
        : (currentIndex + (event.key === 'ArrowRight' ? 1 : -1) + mobileViews.length) % mobileViews.length;
    const nextView = mobileViews[nextIndex];
    setMobileView(nextView);
    document.getElementById(`mobile-tab-${nextView}`)?.focus();
  };

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">Skip to data</a>
      <header className="topbar">
        <a className="brand" href="/" aria-label="AssetGlobe home">
          <span className="brand-mark"><i /><i /><i /></span>
          <span><strong>ASSETGLOBE</strong><small>XRPL</small></span>
        </a>
        <div className="network-state">
          <span className={`connection-dot connection-${ledger.status}`} />
          <span>{statusCopy(ledger.status)}</span>
          {ledger.ledgerIndex && <b>Ledger {Number(ledger.ledgerIndex).toLocaleString()}</b>}
        </div>
        <nav className="topnav" aria-label="Primary">
          <button onClick={() => setMethodologyOpen(true)}>Methodology</button>
          <a href="https://github.com/LJ-XRPL/xrpl-map" target="_blank" rel="noreferrer">GitHub ↗</a>
        </nav>
      </header>

      <main id="main-content">
        <section className="intro-band">
          <div>
            <span className="eyebrow">Public ledger · evidence-led registry</span>
            <h1>Real assets. <em>Actual ledger activity.</em></h1>
            <p>{catalog.status === 'ready'
              ? 'The complete authorized RWA.xyz catalog for XRP Ledger, a higher-trust curated registry, direct balance state, and validated stablecoin and RWA transactions. No simulated flows, no invented market caps.'
              : 'Public XRPL issuer identities with first-party evidence, balances read straight from validated ledger state, and stablecoin and RWA transactions as they settle. No simulated flows, no invented market caps.'}</p>
            <div className="hero-hints" aria-hidden="true">
              <span><kbd>/</kbd> search registry</span>
              <span><kbd>Esc</kbd> close panels</span>
            </div>
          </div>
          <div className="kpi-grid" aria-label="AssetGlobe summary">
            {catalog.status === 'ready'
              ? <div><span>RWA.xyz assets</span><strong>{catalog.meta?.distinctAssets || '—'}</strong><small>{catalogTrackableCount || '—'} live-trackable</small></div>
              : <div><span>Registry programs</span><strong>{registry.length}</strong><small>curated evidence records</small></div>}
            <div><span>Tracked accounts</span><strong>{trackedWalletCount || '—'}</strong><small>exact public identities</small></div>
            <div><span>Curated active</span><strong>{activeInstruments || '—'}</strong><small>non-zero ledger state</small></div>
            <div><span>Live this session</span><strong>{liveFlowCount}</strong><small>validated matches</small></div>
          </div>
        </section>

        <section className="source-rail" aria-label="Data source status">
          <div><span className={`connection-dot connection-${ledger.status}`} /><strong>XRPL mainnet</strong><small>{ledger.ledgerIndex ? `Validated ledger ${Number(ledger.ledgerIndex).toLocaleString()}` : 'Connecting'}</small></div>
          <div><span className={`source-status-dot source-status-${catalogNotConfigured ? 'unavailable' : catalogStale ? 'stale' : catalog.status}`} /><strong>RWA.xyz catalog</strong><small>{catalog.status === 'ready' ? `${catalog.meta.tokenRecords} records · updated ${sourceDate(catalog.meta.sourceUpdatedAt)}` : catalogNotConfigured ? 'Authorized feed not connected' : catalog.status}</small></div>
          <div><span className={`source-status-dot source-status-${discovery.status}`} /><strong>XRPLMeta discovery</strong><small>{discovery.status === 'ready' ? `${discovery.totalMpts} MPT records scanned` : discovery.status}</small></div>
          <button onClick={() => setMethodologyOpen(true)}>How sources differ <span>→</span></button>
        </section>

        {ledger.error && <div className="error-banner" role="status">Some live data is delayed: {ledger.error}</div>}
        {catalog.error && !catalogNotConfigured && <div className="error-banner" role="status">RWA.xyz reference catalog is delayed: {catalog.error} <button onClick={catalog.retry}>Retry</button></div>}
        {catalogStale && <div className="error-banner" role="status">The RWA.xyz identity snapshot is more than seven days old. Live XRPL activity remains current, but catalog membership may have changed.</div>}

        <div className="mobile-tabs" role="tablist" aria-label="Dashboard views">
          {mobileViews.map((view) => (
            <button key={view} id={`mobile-tab-${view}`} role="tab" tabIndex={mobileView === view ? 0 : -1} aria-selected={mobileView === view} aria-controls={`mobile-panel-${view}`} className={mobileView === view ? 'active' : ''} onClick={() => setMobileView(view)} onKeyDown={(event) => moveMobileTab(event, view)}>{view}</button>
          ))}
        </div>

        <section className="workspace-grid">
          <div id="mobile-panel-assets" role={isCompact ? 'tabpanel' : undefined} aria-labelledby={isCompact ? 'mobile-tab-assets' : undefined} hidden={isCompact && mobileView !== 'assets'} className={`mobile-pane pane-assets ${mobileView === 'assets' ? 'mobile-active' : ''}`}>
            <AssetDirectory
              programs={scopedPrograms}
              selectedId={selectedId}
              onSelect={selectProgram}
              scope={scope}
              onScopeChange={setScope}
              discoveryCount={metadataPrograms.length}
              curatedCount={registry.length}
              catalogCount={catalog.meta?.distinctAssets || 0}
              catalogStatus={catalog.status}
            />
          </div>
          <div id="mobile-panel-map" role={isCompact ? 'tabpanel' : undefined} aria-labelledby={isCompact ? 'mobile-tab-map' : undefined} hidden={isCompact && mobileView !== 'map'} className={`map-column mobile-pane ${mobileView === 'map' ? 'mobile-active' : ''}`}>
            {(!isCompact || mobileView === 'map') && (
              <Suspense fallback={<div className="globe-loading"><span />Loading globe geometry…</div>}>
                <AssetGlobe
                  programs={registry}
                  selectedId={selectedId}
                  lastProgramId={ledger.lastProgramId}
                  onSelect={selectProgram}
                />
              </Suspense>
            )}
            <div className="map-integrity-note">
              <span>NO SIMULATED DATA</span>
              <p>Points mark associated organizations. A pulse means a matched transaction, not a geographic route.</p>
            </div>
          </div>
          <div id="mobile-panel-flow" role={isCompact ? 'tabpanel' : undefined} aria-labelledby={isCompact ? 'mobile-tab-flow' : undefined} hidden={isCompact && mobileView !== 'flow'} className={`mobile-pane pane-flow ${mobileView === 'flow' ? 'mobile-active' : ''}`}>
            <FlowFeed
              flows={ledger.flows}
              status={ledger.status}
              ledgerIndex={ledger.ledgerIndex}
              endpoint={ledger.endpoint}
              onSelect={selectProgram}
            />
          </div>
        </section>

        {scope === 'catalog' ? (
          <CatalogTable
            programs={catalogPrograms}
            flows={ledger.flows}
            meta={catalog.meta}
            status={catalog.status}
            error={catalog.error}
            errorCode={catalog.errorCode}
            retry={catalog.retry}
            onSelect={selectProgram}
          />
        ) : (
          <SupplyTable
            supplies={scopedSupplies}
            ledgerIndex={ledger.ledgerIndex}
            snapshotAt={ledger.snapshotAt}
            onSelect={selectProgram}
            loading={ledger.status === 'connecting'}
          />
        )}

        <section className="coverage-strip">
          <div>
            <span className="eyebrow">Honest coverage</span>
            <h2>“Global” needs a boundary.</h2>
          </div>
          <p>{catalog.status === 'ready'
            ? 'AssetGlobe exposes the complete authorized RWA.xyz XRPL catalog, but keeps those third-party classifications separate from independently curated identities. Private accounts, unpublished issuers and permissioned activity still cannot be inferred from the public ledger.'
            : 'AssetGlobe keeps first-party and corroborated identities separate from third-party catalogs. Full RWA.xyz coverage remains fail-closed until an API agreement permits public display; private accounts, unpublished issuers and permissioned activity cannot be inferred from the public ledger.'}</p>
          <button onClick={() => setMethodologyOpen(true)}>Read the methodology <span>→</span></button>
        </section>
      </main>

      <footer>
        <span>AssetGlobe · XRPL mainnet</span>
        <span>Registry audit {registryAuditDate} · RWA.xyz source {catalog.meta?.sourceUpdatedAt?.slice(0, 10) || (catalog.status === 'error' ? 'not connected' : 'loading')}</span>
        <span>Ledger data is factual; issuer claims are linked, not adopted.</span>
      </footer>

      <DetailDrawer
        program={drawerOpen ? selectedProgram : null}
        supplies={allSupplies}
        flows={ledger.flows}
        onClose={closeDrawer}
      />
      <Methodology
        open={methodologyOpen}
        onClose={closeMethodology}
        auditDate={registryAuditDate}
        discovery={{ ...discovery, programs: metadataPrograms }}
        catalog={{ ...catalog, programs: catalogPrograms }}
      />
      <Analytics />
    </div>
  );
}
